<script lang="ts" setup>
import { useClaims } from '@auth';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { menuQueries } from '@services/asp-c/queries/Menu';
import { VBreadcrumb } from '@shared/component';
import { useQuery } from '@tanstack/vue-query';
import { computed, watch } from 'vue';
import { useBreadcrumbStore } from '../../breadcrumb/breadcrumbStore';
import { menu, MenuItem } from '../../store/menu';
import { tryMenuNavigation } from '../../urlHelper';
import AppLayoutCrvp from './AppLayoutCrvp.vue';
import AppLayoutLogo from './AppLayoutLogo/AppLayoutLogo.vue';
import AppLayoutMenuAccount from './AppLayoutMenuAccount.vue';
import AppLayoutMenuContact from './AppLayoutMenuContact.vue';
import AppLayoutMenuIntern from './AppLayoutMenuIntern.vue';
import AppLayoutMenuMain from './AppLayoutMenuMain.vue';

const props = defineProps<{
  customMenuItems?: Array<MenuItem>;
  customMenuLoadingState?: boolean;
  navigateInFrame?: boolean;
}>();

const breadcrumbStore = useBreadcrumbStore();
const claims = useClaims();
const { data: ratio } = useQuery({
  ...menuQueries.getCrvp(),
  select: result => result.data,
  enabled: computed(() => claims.value?.isSuccess === true)
});

watch(
  () => claims.value?.isWebmodule,
  isWebmodule => {
    const { style } = document.documentElement;
    const variableName = '--header-height';

    if (isWebmodule) {
      style.setProperty(variableName, '0');
    } else {
      style.removeProperty(variableName);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="tw-grow tw-text-primary-900"
    :class="{
      'tw-grid tw-grid-cols-[auto_1fr] tw-grid-rows-[var(--header-height,0px)_1fr] max-lg:before:tw-absolute max-lg:before:tw-h-16 max-lg:before:tw-w-full max-lg:before:tw-shadow-sm max-lg:before:tw-content-[\'\']':
        !claims?.isWebmodule
    }">
    <template v-if="!claims?.isWebmodule">
      <div
        class="tw-sticky tw-top-0 tw-z-header tw-flex tw-max-w-60 tw-border-0 tw-border-b tw-border-solid tw-border-grey-200 tw-bg-white tw-px-2 lg:tw-border-r lg:tw-px-4">
        <button
          v-if="!claims?.isTemporaryUser"
          class="tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-px-2 tw-text-lg tw-text-inherit tw-outline-none"
          type="button"
          @click="menu.toggle">
          <FontAwesomeIcon :icon="['fas', 'bars']" />
        </button>

        <AppLayoutLogo class="tw-min-w-0 tw-px-2" />
      </div>

      <header
        class="tw-sticky tw-top-0 tw-z-header tw-flex tw-justify-end tw-border-0 tw-border-b tw-border-solid tw-border-grey-200 tw-bg-white tw-pl-2 lg:tw-pl-6">
        <VBreadcrumb v-if="!claims?.isTemporaryUser" class="tw-grow" :items="breadcrumbStore.breadcrumbs" />

        <nav class="tw-flex lg:tw-divide-x lg:tw-divide-y-0 lg:tw-divide-solid lg:tw-divide-grey-200">
          <template v-if="!claims?.isTemporaryUser">
            <a
              class="tw-hidden tw-items-center tw-gap-ch tw-px-6 tw-text-sm tw-text-inherit lg:tw-flex"
              href="/net/home/about"
              @click="tryMenuNavigation">
              <strong>Vragen?</strong>
              Bel ons op (072) 30 35 900
            </a>

            <AppLayoutCrvp
              v-if="ratio"
              class="tw-px-4 max-xl:tw-hidden lg:tw-px-6"
              :color-name="ratio.kleur"
              :rounded-value="ratio.CRVPlopendjaar_round" />

            <AppLayoutMenuIntern v-if="claims?.isInternal" />

            <AppLayoutMenuAccount />
          </template>

          <template v-else>
            <AppLayoutMenuContact />

            <a
              class="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-text-sm tw-text-inherit tw-no-underline lg:tw-px-6"
              :href="`/identity/authentication/logout?returnUrl=${encodeURIComponent('/identity/user/verlaten')}`">
              <FontAwesomeIcon :icon="['far', 'right-from-bracket']" />
              <span class="max-sm:tw-hidden">Uitloggen</span>
            </a>
          </template>
        </nav>
      </header>
    </template>

    <AppLayoutMenuMain
      v-if="!claims?.isTemporaryUser"
      :custom-items="props.customMenuItems"
      :custom-loading-state="props.customMenuLoadingState"
      :navigate-in-frame="props.navigateInFrame" />

    <div class="tw-col-span-2 tw-grid">
      <slot></slot>
    </div>
  </div>
</template>
